import { useEffect, useMemo, useRef } from "react";
import { Node, useReactFlow, useViewport } from "@xyflow/react";
import useOrgchartEmployeesStore from "src/Pages/Company/OrganizationStructure/ConfigureStructure/Tabs/StructureView/Employees/store";
import { QuadTree } from "src/Pages/Company/OrganizationStructure/ConfigureStructure/Tabs/StructureView/Employees/utils/QuadTree";

export const useQuadTreeVisibility = (nodes: Node[]) => {
  const { getViewport } = useReactFlow();
  const viewport = useViewport();
  const { setVisibleNodesRef } = useOrgchartEmployeesStore();
  const rafRef = useRef<number>(0);
  const lastUpdateRef = useRef<Map<string, boolean>>(new Map());

  const quadTree = useMemo(() => {
    if (nodes.length === 0) return null;

    const minX = Math.min(...nodes.map((n) => n.position.x));
    const minY = Math.min(...nodes.map((n) => n.position.y));
    const maxX = Math.max(
      ...nodes.map((n) => n.position.x + (n.measured?.width || 340))
    );
    const maxY = Math.max(
      ...nodes.map((n) => n.position.y + (n.measured?.height || 150))
    );

    const tree = new QuadTree({
      x: minX - 1000,
      y: minY - 1000,
      width: maxX - minX + 2000,
      height: maxY - minY + 2000,
    });

    nodes.forEach((node) => tree.insert(node));
    return tree;
  }, [nodes]);

  useEffect(() => {
    if (!quadTree) return undefined;

    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
    }

    rafRef.current = requestAnimationFrame(() => {
      const vp = getViewport();
      const viewportBounds = {
        x: -vp.x / vp.zoom,
        y: -vp.y / vp.zoom,
        width: window.innerWidth / vp.zoom,
        height: window.innerHeight / vp.zoom,
      };

      const visibleNodes = quadTree.query(viewportBounds);
      const visibilityMap = new Map<string, boolean>();

      visibleNodes.forEach((node) => {
        visibilityMap.set(node.id, true);
      });

      if (lastUpdateRef.current.size !== visibilityMap.size) {
        lastUpdateRef.current = visibilityMap;
        setVisibleNodesRef(visibilityMap);
        return;
      }

      const hasChanged = Array.from(visibilityMap.entries()).some(
        ([id, visible]) => lastUpdateRef.current.get(id) !== visible
      );

      if (hasChanged) {
        lastUpdateRef.current = visibilityMap;
        setVisibleNodesRef(visibilityMap);
      }
    });

    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [quadTree, viewport, nodes, getViewport, setVisibleNodesRef]);
};
